import React, { useRef, useEffect } from 'react';
import Input from '../../ui/Input';
import Label from '../../ui/Label';
import Select from '../../ui/Select';

const AutoInvoiceDetails = ({ 
  formData = {}, 
  handleInputChange = () => {}, 
  bankDetails = [],
  suggestions = [],
  showSuggestions = false,
  searchClients = () => {},
  clearSuggestions = () => {}
}) => {
  
  const suggestionsRef = useRef(null);

  useEffect(() => {
      const handleClickOutside = (event) => {
          if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
              clearSuggestions();
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
          document.removeEventListener('mousedown', handleClickOutside);
      };
  }, [clearSuggestions]);

  return (
    <div className="p-4 sm:p-6 border-b border-gray-100">
      
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-x-6 gap-y-5">
        
        {/* Row 1 */}
        <div>
          <Label>Начална дата</Label>
          <Input 
            type="date" 
            name="nextInvoiceDate"
            value={formData.nextInvoiceDate || ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="relative" ref={suggestionsRef}>
          <Label>Име</Label>
          <Input 
            name="receiverName"
            placeholder="Дженерал Сълюшън ЕООД"
            value={formData.receiverName || ''}
            onChange={(e) => {
                handleInputChange(e);
                searchClients(e.target.value);
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {suggestions.map((client, index) => (
                      <li
                          key={index}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          onClick={() => {
                              handleInputChange({ target: { name: 'receiverName', value: client.name } });
                              handleInputChange({ target: { name: 'receiverIdNumber', value: client.eik } });
                              handleInputChange({ target: { name: 'receiverVatNumber', value: client.vatNumber } });
                              handleInputChange({ target: { name: 'receiverMol', value: client.mol } });
                              handleInputChange({ target: { name: 'receiverCity', value: client.city } });
                              handleInputChange({ target: { name: 'receiverAddress', value: client.address } });
                              handleInputChange({ target: { name: 'receiverPhoneNumber', value: client.phoneNumber } });
                              clearSuggestions();
                          }}
                      >
                          {client.name}
                      </li>
                  ))}
              </ul>
          )}
        </div>
        <div>
          <Label>МОЛ</Label>
          <Input 
            name="receiverMol"
            placeholder="Иван Иванов" 
            value={formData.receiverMol || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Row 2 */}
        <div>
          <Label>Повтаряй на всеки</Label>
          <Input 
            type="number" 
            name="interval"
            min="1"
            value={formData.interval || '1'} 
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>ЕИК</Label>
          <Input 
            name="receiverIdNumber"
            placeholder="123456789" 
            value={formData.receiverIdNumber || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Телефон</Label>
          <Input 
            type="tel"
            name="receiverPhoneNumber"
            placeholder="0878000000" 
            value={formData.receiverPhoneNumber || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Row 3 */}
        <div>
          <Label>Период</Label>
          <Select 
            name="scheduleType"
            value={formData.scheduleType || 'MONTHLY'}
            onChange={handleInputChange}
          >
            <option value="DAILY">дни</option>
            <option value="WEEKLY">седмици</option>
            <option value="MONTHLY">месеци</option>
          </Select>
        </div>
        <div>
          <Label>Град</Label>
          <Input 
            name="receiverCity"
            placeholder="София" 
            value={formData.receiverCity || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>ДДС номер</Label>
          <Input 
            name="receiverVatNumber"
            placeholder="BG123456789" 
            value={formData.receiverVatNumber || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Row 4 */}
        <div className="hidden 2xl:block"></div>
        <div>
          <Label>Адрес</Label>
          <Input 
            name="receiverAddress"
            placeholder="Бул. Стамболийски 8" 
            value={formData.receiverAddress || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Плащане</Label>
          <Select 
            name="paymentMethod"
            value={formData.paymentMethod || 'CASH'}
            onChange={handleInputChange}
          >
            <option value="CASH">В брой</option>
            <option value="BANK_TRANSFER">По банков път</option>
          </Select>
        </div>

        {/* Row 5 - Bank details conditional */}
        {formData.paymentMethod === 'BANK_TRANSFER' && (
          <>
            <div className="hidden 2xl:block"></div>
            <div className="hidden 2xl:block"></div>
            <div>
              <Label>Банкова сметка</Label>
              <Select
                name="issuerBankDetailsId"
                value={formData.issuerBankDetailsId || ''}
                onChange={handleInputChange}
              >
                <option value="" disabled>Изберете банкова сметка</option>
                {bankDetails && bankDetails.map(bd => (
                    <option key={bd.id} value={bd.id}>{bd.iban}</option>
                ))}
              </Select>
              {formData.issuerBankDetailsId && bankDetails && bankDetails.find(bd => bd.id === formData.issuerBankDetailsId) && (
                  <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-md border border-gray-100">
                      <div><span className="font-semibold text-gray-700">Банка:</span> {bankDetails.find(bd => bd.id === formData.issuerBankDetailsId).bank}</div>
                      <div className="mt-1"><span className="font-semibold text-gray-700">BIC:</span> {bankDetails.find(bd => bd.id === formData.issuerBankDetailsId).bankCode}</div>
                  </div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default AutoInvoiceDetails;
